import React, { useState } from 'react';
import { HowCalculated } from './MetricsUI';

/**
 * A collapsible page section.
 *
 * The heading and a `summary` stay visible; everything else opens on the
 * chevron. The page has four sections and each carries several charts, so shown
 * all at once it was a very long scroll where nothing could be compared — a
 * manager had to remember the adoption numbers by the time they reached the
 * scores. The summary is the part worth seeing without asking.
 */
export default function Section({
  title,
  subtitle,
  help,
  helpWide,
  summary,
  children,
  defaultOpen = false,
  right,
  /**
   * Draw a rule above this section.
   *
   * Opt-in rather than automatic, because a section is not always the first
   * thing on its page: on the admin console each tab renders exactly one, and a
   * rule above it would hang under the tab bar with nothing to separate.
   */
  divider = false,
  /** Wording for the expander under the summary. */
  detailLabel = 'View more details',
}) {
  const [open, setOpen] = useState(defaultOpen);
  const hasBody = !!children;

  /*
   * The whole heading is the control, with one rotating chevron and no wording.
   *
   * A labelled "Show detail" button read as page copy rather than a control —
   * four of them down a page made it look like the sections were annotated. A
   * chevron on a clickable heading is the pattern people already know from every
   * accordion, and the heading is a far bigger target than an icon on its own.
   *
   * The `?` sits outside the clickable area: clicking it must open the
   * explanation, not collapse the section underneath it.
   */
  const Chevron = () => (
    <svg
      className={`h-5 w-5 shrink-0 transition-transform group-hover:text-navy ${
        open ? 'rotate-180' : ''
      }`}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M5.5 7.5a1 1 0 011.4 0L10 10.6l3.1-3.1a1 1 0 111.4 1.4l-3.8 3.8a1 1 0 01-1.4 0L5.5 8.9a1 1 0 010-1.4z" />
    </svg>
  );

  return (
    <section className={divider ? 'border-t border-gray-200 pt-10' : undefined}>
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            {hasBody ? (
              <button
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                title={open ? 'Hide detail' : 'Show detail'}
                className="group flex items-center gap-1.5 text-left"
              >
                <h2 className="text-2xl font-bold text-navy group-hover:text-lavender">{title}</h2>
                <span className="text-gray-400">
                  <Chevron />
                </span>
              </button>
            ) : (
              <h2 className="text-2xl font-bold text-navy">{title}</h2>
            )}
            {help && (
              <HowCalculated wide={helpWide} label="What this means">
                {help}
              </HowCalculated>
            )}
          </div>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        {right}
      </div>

      {summary}

      {/*
        * A second way into the same detail, under the summary rather than on
        * the heading.
        *
        * The chevron is discoverable only if you already suspect something is
        * hidden — people read the summary tiles and stopped, never learning the
        * section opened at all. This sits directly below those tiles, centred,
        * in the same purple the numbers use, and says what it does. The chevron
        * stays: once you know, a heading is a much bigger target than a button.
        */}
      {hasBody && (
        <div className="mt-5 flex justify-center">
          <button
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="group inline-flex items-center gap-1.5 rounded-full border border-lavender/30 bg-white px-5 py-2 text-sm font-semibold text-lavender transition-colors hover:border-lavender hover:bg-lavender/5"
          >
            {open ? 'Hide details' : detailLabel}
            <Chevron />
          </button>
        </div>
      )}

      {open && hasBody && <div className="mt-6 space-y-6">{children}</div>}
    </section>
  );
}
